import { Navigate, Route, BrowserRouter, Routes } from 'react-router-dom';
import { AuthProvider, SkipToContentLink, lazyNamed, lazyRoute } from '@monecolier/ui';
import { apiClient } from './services/api';
import { Sidebar } from './components/Sidebar';
import { PushNotificationRegistration } from './components/PushNotificationRegistration';
import { RequireRouteAccess } from './components/RequireRouteAccess';
import { AccessRedirectNotice } from './components/AccessRedirectNotice';
import { AppBreadcrumbs } from './components/AppBreadcrumbs';

/*
 * Découpage de code par route (audit Impeccable, 2026-09-07).
 *
 * AVANT : les ~40 pages ci-dessous étaient toutes importées statiquement, et
 * `vite build` produisait un chunk unique de 606,66 kB (157,19 kB gzip) —
 * ouvrir `/login` téléchargeait le dashboard financier, le journal d'audit et
 * l'assistant d'onboarding. PRODUCT.md cible des écoles en 4G/5G, mais la
 * connexion réelle d'un établissement reste irrégulière.
 *
 * CE QUI RESTE STATIQUE, délibérément : les 6 écrans d'entrée
 * (Home/Login/Register/ForgotPassword/ResetPassword/NotFound). Ce sont les
 * seuls atteignables sans être authentifié, donc ceux du tout premier
 * affichage : les rendre paresseux ajouterait un aller-retour réseau AVANT le
 * premier pixel utile, exactement l'inverse du but recherché. Tout le reste
 * est derrière une authentification, donc jamais sur le chemin critique du
 * premier rendu.
 *
 * `lazyNamed` (packages/ui/src/lazyRoute.tsx) plutôt que `React.lazy` direct :
 * toutes les pages de ce dépôt sont des exports NOMMÉS, `React.lazy` exige un
 * export défaut. Voir ce fichier pour le détail, dont le choix d'une
 * `<Suspense>` PAR ROUTE (la Sidebar reste montée et navigable pendant le
 * téléchargement, au lieu d'être démontée par une frontière unique).
 */
import { Home } from './pages/Home';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { NotFound } from './pages/NotFound';

const CreateSchool = lazyNamed(() => import('./pages/CreateSchool'), 'CreateSchool');
const MfaSetup = lazyNamed(() => import('./pages/MfaSetup'), 'MfaSetup');
const Account = lazyNamed(() => import('./pages/Account'), 'Account');
const ChangePassword = lazyNamed(() => import('./pages/ChangePassword'), 'ChangePassword');
const Profile = lazyNamed(() => import('./pages/Profile'), 'Profile');
const SettingsOverview = lazyNamed(() => import('./pages/SettingsOverview'), 'SettingsOverview');
const OnboardingWizard = lazyNamed(() => import('./pages/OnboardingWizard'), 'OnboardingWizard');
const SchoolBranding = lazyNamed(() => import('./pages/SchoolBranding'), 'SchoolBranding');
const ManageClasses = lazyNamed(() => import('./pages/ManageClasses'), 'ManageClasses');
const SchoolYears = lazyNamed(() => import('./pages/SchoolYears'), 'SchoolYears');
const StudentsOverview = lazyNamed(() => import('./pages/StudentsOverview'), 'StudentsOverview');
const Students = lazyNamed(() => import('./pages/Students'), 'Students');
const StudentDetail = lazyNamed(() => import('./pages/StudentDetail'), 'StudentDetail');
const EnrollmentDetail = lazyNamed(() => import('./pages/EnrollmentDetail'), 'EnrollmentDetail');
const StudentDuplicates = lazyNamed(() => import('./pages/StudentDuplicates'), 'StudentDuplicates');
const Fees = lazyNamed(() => import('./pages/Fees'), 'Fees');
const PaymentMethods = lazyNamed(() => import('./pages/PaymentMethods'), 'PaymentMethods');
const DocumentRequirements = lazyNamed(() => import('./pages/DocumentRequirements'), 'DocumentRequirements');
const Invitations = lazyNamed(() => import('./pages/Invitations'), 'Invitations');
const WhatsAppBroadcasts = lazyNamed(() => import('./pages/WhatsAppBroadcasts'), 'WhatsAppBroadcasts');
const AcceptInvitation = lazyNamed(() => import('./pages/AcceptInvitation'), 'AcceptInvitation');
const Caisse = lazyNamed(() => import('./pages/Caisse'), 'Caisse');
const PaymentHistory = lazyNamed(() => import('./pages/PaymentHistory'), 'PaymentHistory');
const AccountingOverview = lazyNamed(() => import('./pages/AccountingOverview'), 'AccountingOverview');
const FinancialDashboard = lazyNamed(() => import('./pages/FinancialDashboard'), 'FinancialDashboard');
const FinancialByClass = lazyNamed(() => import('./pages/FinancialByClass'), 'FinancialByClass');
const FinancialByClassStudents = lazyNamed(
  () => import('./pages/FinancialByClassStudents'),
  'FinancialByClassStudents',
);
const OverdueInvoices = lazyNamed(() => import('./pages/OverdueInvoices'), 'OverdueInvoices');
const AuditLogs = lazyNamed(() => import('./pages/AuditLogs'), 'AuditLogs');
const CreditLedger = lazyNamed(() => import('./pages/CreditLedger'), 'CreditLedger');
const PaymentProofs = lazyNamed(() => import('./pages/PaymentProofs'), 'PaymentProofs');
const Enrollments = lazyNamed(() => import('./pages/Enrollments'), 'Enrollments');
const TeacherHome = lazyNamed(() => import('./pages/TeacherHome'), 'TeacherHome');
const ClassTimetable = lazyNamed(() => import('./pages/ClassTimetable'), 'ClassTimetable');
const ClassTeacherAssignments = lazyNamed(() => import('./pages/ClassTeacherAssignments'), 'ClassTeacherAssignments');
const TeacherClasses = lazyNamed(() => import('./pages/TeacherClasses'), 'TeacherClasses');
const TeacherSchedule = lazyNamed(() => import('./pages/TeacherSchedule'), 'TeacherSchedule');
const TeacherAttendance = lazyNamed(() => import('./pages/TeacherAttendance'), 'TeacherAttendance');
const TeacherHomework = lazyNamed(() => import('./pages/ComingSoonPage'), 'TeacherHomework');
// EVA-04 : "Notes" a désormais un contenu réel (saisie des notes + pondération
// par type d'évaluation) — retiré du groupe ComingSoonPage ci-dessus.
const TeacherGrades = lazyNamed(() => import('./pages/TeacherGrades'), 'TeacherGrades');
const TeacherMessaging = lazyNamed(() => import('./pages/ComingSoonPage'), 'TeacherMessaging');

export function App() {
  return (
    <AuthProvider apiClient={apiClient}>
      {/* MOB-06 : composant invisible, monté UNE SEULE FOIS — enregistre le
          jeton device (push natif) dès qu'un compte authentifié est
          disponible, no-op complet hors coquille Capacitor native (voir
          hooks/usePushNotificationRegistration.ts). */}
      <PushNotificationRegistration />
      {/* FND-23 : basename dérivé de la `base` Vite (défaut '/', '/office/' en
          production derrière Caddy — voir vite.config.ts) — import.meta.env.BASE_URL
          est calculé automatiquement par Vite à partir de ce `base`, aucune variable
          dédiée côté routeur. */}
      <BrowserRouter basename={import.meta.env.BASE_URL}>
        {/* UX-09 : `#root` est désormais une rangée (sidebar + contenu, voir
            index.css) plutôt qu'une colonne (UX-01) — Sidebar monté UNE SEULE FOIS,
            persiste donc sur toute navigation (jamais démonté/remonté), voir
            components/Sidebar.tsx (remplace NavBar.tsx/UX-04, remplacement DIRECT). */}
        {/* Lien d'évitement (audit Impeccable, 2026-09-07) — PREMIER élément
            focusable du document, donc AVANT <Sidebar /> : placé après, il
            n'éviterait plus rien. Voir packages/ui/src/SkipToContentLink.tsx
            (WCAG 2.1 A, 2.4.1 Bypass Blocks). */}
        <SkipToContentLink label="Aller au contenu principal" />
        <Sidebar />
        {/* UX-02 : `px-2 sm:px-4` — marge horizontale réduite sous 640px, revient à
            la marge d'origine (`px-4`, INCHANGÉE) dès `sm:` — sur mobile, chaque
            pixel compte pour les tableaux denses (Students/Fees/ManageClasses/
            voir docs/architecture.md section "UX-02"). */}
        {/* Correctif défaut de disposition (signalé en staging, 09/08) : `justify-
            center` centrait VERTICALEMENT tout contenu de route dans ce <main> —
            correct pour les petites pages d'auth (Card size="form", ex. Login/
            Register), mais pousse chaque page "page" (Card size="page" — Students,
            Fees, Enrollments, Payments... 16 pages au 09/08) vers le bas dès que son
            contenu est plus court que la hauteur du viewport, au lieu de rester
            ancrée en haut comme une vraie page d'application. `justify-start`
            (jamais évalué avant ce correctif) aligne tout en haut par défaut — les
            pages d'auth restent centrées HORIZONTALEMENT (`items-center` inchangé),
            juste plus haut que le milieu de l'écran, comportement standard pour un
            SaaS (pas une régression visuelle jugée problématique, contrairement au
            bug signalé). */}
        {/* UX-55 : `min-w-0` ajouté — `<main>` est un enfant flex de `#root`
            (`flex lg:flex-row`, voir index.css) ; sans cette ligne, le
            défaut CSS `min-width: auto` d'un enfant flex l'empêche de
            rétrécir sous la largeur MINIMALE de son contenu le plus large
            (ex. un `<select>` de filtre), le forçant à déborder de quelques
            pixels au-delà de la sidebar — mesuré RÉELLEMENT (Playwright,
            `document.documentElement.scrollWidth` > `innerWidth` de 4px sur
            `/overdue-invoices`, présent aux DEUX largeurs ≥1024px testées,
            AVANT ce correctif). Chaque tableau gère déjà son propre débordement
            via un conteneur `overflow-x-auto` dédié (UX-02/UX-48) — `<main>`
            lui-même n'a besoin d'aucune largeur minimale propre, `min-w-0`
            laisse simplement ce mécanisme déjà en place fonctionner comme
            prévu plutôt que de déborder la page entière avant d'y arriver. */}
        <main id="contenu-principal" className="flex min-w-0 flex-1 flex-col items-center justify-start px-2 py-6 sm:px-4 sm:py-10">
          {/* UX-79 : bandeau "pas accès à cette page", affiché sur l'accueil après
              une redirection de <RequireRouteAccess> ci-dessous. */}
          <AccessRedirectNotice />
          {/* UX-79 : garde de rôle UNIQUE pour toutes les routes, pilotée par
              `ROUTE_ACCESS` (lib/roles.ts) — redirige vers l'accueil au lieu de
              laisser la page afficher le 403 du backend. */}
          <RequireRouteAccess>
            {/* UX-85 : fil d'Ariane, monté UNE SEULE FOIS juste au-dessus de
                <Routes> — DANS la garde de rôle, pour ne jamais s'afficher
                sur une page dont la redirection est en cours. Voir
                components/AppBreadcrumbs.tsx et breadcrumbs.ts. */}
            <AppBreadcrumbs>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                {/* SCH-01 : reste accessible même sans Membership (voir AccessDenied.tsx,
                    qui y renvoie explicitement) — la "protection" réelle est côté backend
                    (POST /schools exige requireAuth, voir routes/schools.ts). */}
                <Route path="/schools/new" element={lazyRoute(<CreateSchool />)} />
                {/* AUT-05 : accessible à TOUT compte authentifié (requireAuth seul côté
                    backend, jamais requireRole — voir routes/mfa.ts) — délibérément PAS
                    restreint à SCHOOL_ADMIN ici, même si seul le lien Sidebar (canManage)
                    y mène en pratique. */}
                <Route path="/mfa-setup" element={lazyRoute(<MfaSetup />)} />
                {/* UX-42 : page hub "Compte" — SEUL point d'entrée Sidebar désormais
                    (voir components/Sidebar.tsx), remplace les deux anciens liens
                    plats "Mes informations personnelles"/"Changer mon mot de
                    passe". Même garde qu'eux (isAuthenticated seul, aucune garde
                    de rôle) — voir Account.tsx. */}
                <Route path="/account" element={lazyRoute(<Account />)} />
                {/* AUT-16 : accessible à TOUT compte authentifié (requireAuth seul
                    côté backend, voir routes/auth.ts#POST /auth/change-password) —
                    comme /mfa-setup ci-dessus. Route INCHANGÉE par UX-42 (le hub
                    /account y mène désormais, mais /change-password reste un lien
                    profond valide indépendant). */}
                <Route path="/change-password" element={lazyRoute(<ChangePassword />)} />
                {/* PAR-14 : même auto-garde/emplacement que /change-password
                    ci-dessus (requireAuth seul côté backend, voir routes/
                    auth.ts#GET|PATCH /auth/profile) — nom, téléphone, date de
                    naissance. Route INCHANGÉE par UX-42, voir le commentaire de
                    /change-password ci-dessus. */}
                <Route path="/profile" element={lazyRoute(<Profile />)} />
                {/* UX-05 : même auto-garde que /classes (résout tenantId via GET /me) —
                    tableau de bord d'aperçu du menu Paramètres, voir SettingsOverview.tsx. */}
                <Route path="/settings" element={lazyRoute(<SettingsOverview />)} />
                {/* SCH-05 : même auto-garde que /settings (résout tenantId via
                    GET /me) — assistant d'onboarding guidé (4 étapes). */}
                <Route path="/onboarding" element={lazyRoute(<OnboardingWizard />)} />
                {/* SCH-02 : même auto-garde que /settings (résout tenantId via
                    GET /me) — nom/devise/logo de l'établissement. */}
                <Route path="/branding" element={lazyRoute(<SchoolBranding />)} />
                {/* SCH-03 : résout lui-même le tenantId via GET /me au montage (pas de
                    :id dans l'URL) — mêmes raisons d'auto-garde que /schools/new. */}
                <Route path="/classes" element={lazyRoute(<ManageClasses />)} />
                {/* SCH-11 : même auto-garde que /classes (résout tenantId via GET /me) —
                    création/activation/désactivation d'année scolaire + phases
                    d'inscription. */}
                <Route path="/school-years" element={lazyRoute(<SchoolYears />)} />
                {/* UX-06 : même auto-garde que /settings (résout tenantId via GET /me) —
                    tableau de bord d'aperçu du menu Élèves, voir StudentsOverview.tsx. */}
                <Route path="/students-overview" element={lazyRoute(<StudentsOverview />)} />
                {/* UX-47 : `/reenrollments` (page dédiée depuis UX-18) est FUSIONNÉE dans
                    l'onglet "Réinscriptions" de `/enrollments` — route CONSERVÉE en
                    redirection vers cet onglet précis (jamais un 404 pour un signet déjà
                    enregistré), même précédent que UX-09/UX-15/UX-16/UX-26/UX-33/UX-38. */}
                <Route path="/reenrollments" element={<Navigate to="/enrollments?tab=reenrollments" replace />} />
                {/* SCH-06 : même auto-garde que /classes (résout tenantId via GET /me). */}
                <Route path="/students" element={lazyRoute(<Students />)} />
                {/* UX-80 : fiche élève en page dédiée (ex-tiroir de /students) —
                    garde `ROUTE_ACCESS` identique à `/students` (drill-down DSH-04). */}
                <Route path="/students/:studentId" element={lazyRoute(<StudentDetail />)} />
                {/* SCH-31 : atteinte via le bandeau de Students.tsx, PAS un lien Sidebar
                    permanent — même précédent que /students/missing-invoices (PAY-17). */}
                <Route path="/student-duplicates" element={lazyRoute(<StudentDuplicates />)} />
                {/* SCH-04/FOU-01/PAY-10 : même auto-garde que /classes (résout tenantId
                    via GET /me) — UX-15 a fusionné le catalogue de fournitures
                    (FOU-01, ex-/supply-catalog) DANS cette page, en onglet, voir
                    Fees.tsx. */}
                <Route path="/fees" element={lazyRoute(<Fees />)} />
                {/* UX-15 : `SupplyCatalog.tsx` (FOU-01) retiré — son contenu est
                    désormais fusionné dans `/fees` (Fees.tsx, onglet "Fournitures"),
                    voir ce fichier pour le détail de la décision. Route CONSERVÉE en
                    redirection (même précédent que /reenrollment-status, UX-09) pour
                    tout lien profond/favori déjà existant vers cette ancienne URL. */}
                <Route path="/supply-catalog" element={<Navigate to="/fees" replace />} />
                {/* PAY-18 : même auto-garde que /classes (résout tenantId via GET /me) —
                    configuration des moyens de paiement en ligne (Modèle B). */}
                <Route path="/payment-methods" element={lazyRoute(<PaymentMethods />)} />
                {/* ENR-07 : même auto-garde que /classes (résout tenantId via GET /me) —
                    configuration de la liste des documents requis à l'inscription. */}
                <Route path="/document-requirements" element={lazyRoute(<DocumentRequirements />)} />
                {/* AUT-08 : même auto-garde que /classes (résout tenantId via GET /me) —
                    envoi/liste/révocation d'invitations, SCHOOL_ADMIN_ONLY. */}
                <Route path="/invitations" element={lazyRoute(<Invitations />)} />
                <Route path="/whatsapp-broadcasts" element={lazyRoute(<WhatsAppBroadcasts />)} />
                {/* AUT-08 : page cible du lien envoyé par email — accessible SANS
                    Membership (comme /schools/new), la "protection" réelle est côté
                    backend (POST /invitations/accept exige requireAuth uniquement). */}
                <Route path="/accept-invitation" element={lazyRoute(<AcceptInvitation />)} />
                {/* UX-07 : même auto-garde/rôle que /payments (SCHOOL_ADMIN ET CASHIER,
                    voir Sidebar.tsx) — tableau de bord d'aperçu du menu Comptabilité,
                    voir AccountingOverview.tsx. */}
                <Route path="/accounting-overview" element={lazyRoute(<AccountingOverview />)} />
                {/* DSH-01 : SCHOOL_ADMIN_ONLY côté backend (GET /dashboard/
                    financial-overview), CONTRAIREMENT aux autres routes de ce
                    menu — Sidebar.tsx n'affiche ce lien que si `canManage`
                    (jamais `canCollectPayments` seul). */}
                <Route path="/financial-dashboard" element={lazyRoute(<FinancialDashboard />)} />
                {/* DSH-04 : drill-down dashboard → classe → élève, même auto-garde/rôle
                    que /financial-dashboard (FINANCIAL_MANAGERS côté backend, GET
                    /dashboard/financial-overview/by-class(/students)) — voir
                    FinancialByClass.tsx/FinancialByClassStudents.tsx. */}
                <Route path="/financial-by-class" element={lazyRoute(<FinancialByClass />)} />
                <Route path="/financial-by-class/:classId" element={lazyRoute(<FinancialByClassStudents />)} />
                {/* DSH-03 : même auto-garde/rôle que /financial-dashboard
                    (SCHOOL_ADMIN_ONLY côté backend, GET /dashboard/overdue-invoices*) —
                    liste et export des impayés, voir OverdueInvoices.tsx. */}
                <Route path="/overdue-invoices" element={lazyRoute(<OverdueInvoices />)} />
                {/* AUT-07 : même auto-garde/rôle que /financial-dashboard et
                    /overdue-invoices (SCHOOL_ADMIN_ONLY côté backend, GET
                    /audit-logs) — écran de consultation du journal d'audit,
                    voir AuditLogs.tsx. */}
                <Route path="/audit-logs" element={lazyRoute(<AuditLogs />)} />
                {/* PAY-03/FOU-02/PAY-09 : même auto-garde que /classes (résout tenantId ET
                    rôle via GET /me) — accessible à SCHOOL_ADMIN ET CASHIER, voir
                    Sidebar.tsx. UX-16 avait fait de cette route LE point d'entrée UNIQUE
                    de l'encaissement ET de l'historique/avoirs (page "Paiements"
                    fusionnée) — UX-46 (25/08) REVIENT sur cette décision, scinde en
                    "Caisse" (CETTE route, INCHANGÉE — reste l'encaissement guidé, tous
                    les liens "aller payer" existants continuent de fonctionner sans
                    modification) et "Historique des encaissements" (`/payment-history`,
                    NOUVELLE route, juste en dessous). Voir Caisse.tsx pour le détail
                    complet de la répartition. */}
                <Route path="/payments" element={lazyRoute(<Caisse />)} />
                {/* UX-46 : nouvelle page, EXTRAITE de l'ancienne Payments.tsx (historique
                    par élève + création d'avoir, PAY-08) — voir PaymentHistory.tsx. */}
                <Route path="/payment-history" element={lazyRoute(<PaymentHistory />)} />
                {/* PAY-22 : traçabilité comptable du solde créditeur familial (point 3
                    du ticket), voir CreditLedger.tsx. */}
                <Route path="/credit-ledger" element={lazyRoute(<CreditLedger />)} />
                {/* PAY-16 : dépôt/validation/rejet de preuves de paiement (traçabilité
                    pure, jamais d'impact sur Invoice/Payment) — gate PAYMENT_COLLECTORS
                    (même groupe que /payments/Caisse), voir PaymentProofs.tsx. */}
                <Route path="/payment-proofs" element={lazyRoute(<PaymentProofs />)} />
                {/* UX-16 : `SupplyOrderPayments.tsx` (FOU-02) et `CombinedPayments.tsx`
                    (PAY-09) retirés — leur contenu est désormais fusionné dans
                    `/payments` (Caisse.tsx, ex-Payments.tsx). Routes CONSERVÉES en
                    redirection (même précédent que /supply-catalog→/fees, UX-15) pour
                    tout lien profond/favori déjà existant vers ces anciennes URLs. */}
                <Route path="/supply-payments" element={<Navigate to="/payments" replace />} />
                <Route path="/combined-payments" element={<Navigate to="/payments" replace />} />
                {/* ENR-02 : même auto-garde que /classes (résout tenantId ET rôle via
                    GET /me). UX-47 : page à DEUX onglets ("Nouvelles demandes" par défaut,
                    "Réinscriptions" pour SCHOOL_ADMIN) — voir Enrollments.tsx. */}
                <Route path="/enrollments" element={lazyRoute(<Enrollments />)} />
                {/* UX-81 : fiche d'une demande d'inscription (ex-tiroir). */}
                <Route path="/enrollments/:enrollmentId" element={lazyRoute(<EnrollmentDetail />)} />
                {/* UX-09 fusionnait `ReenrollmentStatus.tsx` (ENR-10) dans
                    `/students-overview` ; UX-18 l'a extraite vers `/reenrollments` ;
                    UX-47 l'a fusionnée dans l'onglet "Réinscriptions" de `/enrollments` —
                    cette redirection pointe DIRECTEMENT vers la destination finale, jamais
                    un double saut par `/reenrollments`. */}
                <Route path="/reenrollment-status" element={<Navigate to="/enrollments?tab=reenrollments" replace />} />
                {/* SCH-53 : accueil MINIMAL du rôle "Enseignant" (TEACHER) — même
                    auto-garde que les autres pages (résout le rôle via GET /me),
                    voir TeacherHome.tsx et Sidebar.tsx. */}
                <Route path="/teacher-home" element={lazyRoute(<TeacherHome />)} />
                {/* SCH-65 : les 4 nouvelles entrées du groupe Sidebar "Enseignant"
                    restructuré (compte TEACHER pur) — mêmes auto-gardes que
                    /teacher-home. "Classe"/"Emploi du temps" ont un contenu réel
                    (réutilisent des routes backend déjà existantes) ; "Devoirs"/
                    "Messagerie" restent honnêtement "Bientôt disponible"
                    (ComingSoonPage.tsx, ex-ComingSoonPanel de TeacherHome.tsx).
                    "Notes" a désormais un contenu réel depuis EVA-04. */}
                <Route path="/teacher-classes" element={lazyRoute(<TeacherClasses />)} />
                <Route path="/teacher-schedule" element={lazyRoute(<TeacherSchedule />)} />
                <Route path="/teacher-attendance" element={lazyRoute(<TeacherAttendance />)} />
                <Route path="/teacher-homework" element={lazyRoute(<TeacherHomework />)} />
                <Route path="/teacher-grades" element={lazyRoute(<TeacherGrades />)} />
                <Route path="/teacher-messaging" element={lazyRoute(<TeacherMessaging />)} />
                {/* SCH-57 : emploi du temps d'UNE classe — consultation ouverte
                    à SCHOOL_ADMIN ∪ TEACHER, édition réservée au responsable de
                    cette classe (SCH-52). Le droit réel vient du serveur
                    (`canEdit`), jamais d'un calcul de rôle côté client — voir
                    ClassTimetable.tsx. */}
                <Route path="/classes/:classId/timetable" element={lazyRoute(<ClassTimetable />)} />
                {/* UX-86 : « Enseignant principal » d'une classe en PAGE dédiée
                    (ex-modale « Enseignants » de ManageClasses.tsx, trop
                    étroite) — même garde `ROUTE_ACCESS` et même droit serveur
                    (`canEdit`) que l'emploi du temps ci-dessus. */}
                <Route path="/classes/:classId/teacher" element={lazyRoute(<ClassTeacherAssignments />)} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </AppBreadcrumbs>
          </RequireRouteAccess>
        </main>
      </BrowserRouter>
    </AuthProvider>
  );
}
